//import external from "./../../externalModules.js";
import { BaseAnnotationTool } from "./base/BaseAnnotationTool.js";
// State
import { getToolState } from "component/toolManager/toolState.js";
import toolStyle from "component/toolManager/toolStyle.js";
import toolColors from "component/toolManager/toolColors.js";

// Drawing
import { getNewContext, draw, setShadow } from "./../../draw/index.js";
import drawLinkedTextBox from "./../../draw/drawLinkedTextBox.js";
import drawHandles from "./../../draw/drawHandles.js";
import drawArrow from "./../../draw/drawArrow.js";
import drawLine from "./../../draw/drawLine";
import math from "core/math";
import { lengthCursor } from "../cursors/index.js";
//import { getLogger } from "../../util/logger.js";
//const logger = getLogger("tools:annotation:LengthTool");

/**
 * @public
 * @class LengthTool
 * @memberof Tools.Annotation
 * @classdesc Tool for measuring distances.
 * @extends Tools.Base.BaseAnnotationTool
 */
export default class LengthTool extends BaseAnnotationTool {
  constructor(configuration = {}) {
    const defaultConfig = {
      name: "Length",
      supportedInteractionTypes: ["Mouse", "Touch"],
      svgCursor: lengthCursor
    };
    const initialConfiguration = Object.assign(defaultConfig, configuration);

    super(initialConfiguration);

    this.initialConfiguration = initialConfiguration;
  }

  createNewMeasurement(eventData) {
    const goodEventData =
      eventData && eventData.currentPoints && eventData.currentPoints.image;

    if (!goodEventData) {
      // logger.error(
      //   `required eventData not supplied to tool ${
      //     this.name
      //   }'s createNewMeasurement`
      // );

      return;
    }

    const { x, y } = eventData.currentPoints.image;

    return {
      visible: true,
      active: true,
      color: undefined,
      handles: {
        start: {
          x,
          y,
          highlight: true,
          active: false
        },
        end: {
          x,
          y,
          highlight: true,
          active: true
        },
        textBox: {
          active: false,
          hasMoved: false,
          movesIndependently: false,
          drawnIndependently: true,
          allowedOutsideImage: true,
          hasBoundingBox: true
        }
      },
      willbeRemove: true,
      moveCount: 0
    };
  }

  /**
   *
   *
   * @param {*} element
   * @param {*} data
   * @param {*} coords
   * @returns {Boolean}
   */
  pointNearTool(element, data, coords) {
    const hasStartAndEndHandles =
      data && data.handles && data.handles.start && data.handles.end;
    const validParameters = hasStartAndEndHandles;

    if (!validParameters) {
      // logger.warn(
      //   `invalid parameters supplied to tool ${this.name}'s pointNearTool`
      // );

      return false;
    }

    if (data.visible === false) {
      return false;
    }

    return (
      math.line.lineSegemntDistance(
        element,
        data.handles.start,
        data.handles.end,
        coords
      ) < 25
    );
  }

  /**
   * @description 도구 정보를 이용해 도구 렌더링
   * @method renderToolData
   *
   * @param {EVENT} evt
   * @returns
   * @memberof LengthTool
   */
  renderToolData(evt) {
    const eventData = evt.detail;
    const { handleRadius, drawHandlesOnHover } = this.configuration;
    const toolData = getToolState(evt.currentTarget, this.name);

    if (!toolData) {
      return;
    }

    // We have tool data for this element - iterate over each one and draw it
    //const context = getNewContext(eventData.canvasContext.canvas);
    const context = getNewContext(eventData.canvas);
    const { image, element } = eventData;

    const lineWidth = toolStyle.getToolWidth();

    let rowPixelSpacing;
    let colPixelSpacing;

    if (image) {
      rowPixelSpacing = image.pixelSpacing[0];
      colPixelSpacing = image.pixelSpacing[1];
    } else {
      rowPixelSpacing = 1;
      colPixelSpacing = 1;
    }

    for (let i = 0; i < toolData.data.length; i++) {
      const data = toolData.data[i];

      if (data.visible === false || data.willbeRemove === true) {
        continue;
      }
      const scale = eventData.viewport.scale;
      // eslint-disable-next-line no-loop-func
      draw(context, context => {
        // Configurable shadow
        setShadow(context, this.configuration);

        // Draw the measurement line

        const lineLength = math.point.distance(
          data.handles.start,
          data.handles.end
        );
        const headLength = 15;
        const bothHeadLength = headLength * 2;

        let color = toolColors.getColorIfActive(data);

        if (lineLength < bothHeadLength) {
          drawLine(context, element, data.handles.start, data.handles.end, {
            color,
            lineWidth: 3
          });
        } else {
          const midPoint = {
            x: (data.handles.end.x + data.handles.start.x) / 2,
            y: (data.handles.end.y + data.handles.start.y) / 2
          };
          drawArrow(context, element, midPoint, data.handles.start, {
            color,
            lineWidth: 3,
            minArrowScale: {
              viewScale: scale,
              lowerBoundScale: 0.52
            }
          });
          color = toolColors.getColorIfActive(data);
          drawArrow(context, element, midPoint, data.handles.end, {
            color,
            lineWidth: 3,
            minArrowScale: {
              viewScale: scale,
              lowerBoundScale: 0.52
            }
          });
        }

        const handleColor = toolColors.getHandleColorIfActive(data);
        // Draw the handles
        const handleOptions = {
          //color,
          color: handleColor,
          handleRadius,
          drawHandlesIfActive: drawHandlesOnHover,
          lineWidth: 3
        };

        drawHandles(context, eventData, data.handles, handleOptions);

        // Set rowPixelSpacing and columnPixelSpacing to 1 if they are undefined (or zero)
        const dx =
          (data.handles.end.x - data.handles.start.x) * (colPixelSpacing || 1);
        const dy =
          (data.handles.end.y - data.handles.start.y) * (rowPixelSpacing || 1);

        // Calculate the length, and create the text variable with the millimeters or pixels suffix
        const length = Math.sqrt(dx * dx + dy * dy);

        // Store the length inside the tool for outside access
        data.length = length;

        if (!data.handles.textBox.hasMoved) {
          const coords = {
            x: Math.max(data.handles.start.x, data.handles.end.x)
          };

          // Depending on which handle has the largest x-value,
          // Set the y-value for the text box
          if (coords.x === data.handles.start.x) {
            coords.y = data.handles.start.y;
          } else {
            coords.y = data.handles.end.y;
          }

          data.handles.textBox.x = coords.x;
          data.handles.textBox.y = coords.y;
        }

        // Move the textbox slightly to the right and upwards
        // So that it sits beside the length tool handle
        const xOffset = 10;

        const text = textBoxText(data, rowPixelSpacing, colPixelSpacing);
        const linkColor = toolColors.getLinkColorIfActive(data);
        drawLinkedTextBox(
          context,
          element,
          data.handles.textBox,
          text,
          data.handles,
          textBoxAnchorPoints,
          color,
          linkColor,
          lineWidth,
          xOffset,
          true
        );
      });
    }

    function textBoxText(data, rowPixelSpacing, colPixelSpacing) {
      // Set the length text suffix depending on whether or not pixelSpacing is available
      let suffix = " mm";

      if (!rowPixelSpacing || !colPixelSpacing) {
        suffix = " pixels";
      }

      return `${data.length.toFixed(2)}${suffix}`;
    }

    function textBoxAnchorPoints(handles) {
      const midpoint = {
        x: (handles.start.x + handles.end.x) / 2,
        y: (handles.start.y + handles.end.y) / 2
      };

      return [handles.start, midpoint, handles.end];
    }
  }
}
